import { __awaiter } from 'tslib';
import { detectAllFaces, detectSingleFace, DetectSingleFaceTask, DetectAllFacesTask, loadAgeGenderModel, loadTinyFaceDetectorModel, loadFaceRecognitionModel, loadFaceLandmarkModel, loadFaceExpressionModel, loadMtcnnModel, loadSsdMobilenetv1Model, FaceMatcher, euclideanDistance, fetchImage, resizeResults, FaceDetection, draw } from 'face-api.js';
import { InjectionToken, ɵɵinject, ɵɵdefineInjectable, ɵsetClassMetadata, Injectable, Inject, Optional, ɵɵdirectiveInject, ElementRef, Renderer2, ɵɵdefineComponent, ɵɵviewQuery, ɵɵqueryRefresh, ɵɵloadQuery, ɵɵlistener, ɵɵresolveWindow, ɵɵelement, Component, ViewChild, HostListener, Injector, ɵɵdefineDirective, Directive, ɵɵInheritDefinitionFeature, Input, ɵɵdefineNgModule, ɵɵdefineInjector, ɵɵsetNgModuleScope, NgModule } from '@angular/core';
import { of, fromEvent, interval, BehaviorSubject, Subscription, Subject, combineLatest } from 'rxjs';
import { switchMap, shareReplay, map } from 'rxjs/operators';
import { OverlayConfig, Overlay, OverlayModule } from '@angular/cdk/overlay';
import { ComponentPortal, PortalModule } from '@angular/cdk/portal';
import { BrowserModule } from '@angular/platform-browser';

class DetectTask {
    constructor(option) {
        this.type = option.type;
        this.tokens = option.tokens;
        this.draw = option.draw;
        this.realtime = option.realtime || false;
        this.target = new Promise(resolver => (this.targetResolver = resolver));
    }
    get resolveTarget() {
        return this.targetResolver;
    }
    isMatchPattern(patten, target) {
        return patten.every(item => target.includes(item));
    }
    with(...tokens) {
        this.tokens.push(...tokens);
        return this;
    }
    detect(option) {
        return __awaiter(this, void 0, void 0, function* () {
            let t;
            if (this.type === 'all') {
                t = detectAllFaces(yield this.target, option || undefined);
            }
            else {
                t = detectSingleFace(yield this.target, option || undefined);
            }
            if (this.isMatchPattern(['expressions', 'landmarks', 'ageAndGender', 'descriptors'], this.tokens)) {
                if (t instanceof DetectSingleFaceTask) {
                    return t
                        .withFaceLandmarks()
                        .withFaceExpressions()
                        .withAgeAndGender()
                        .withFaceDescriptor()
                        .run();
                }
                else if (t instanceof DetectAllFacesTask) {
                    return t
                        .withFaceLandmarks()
                        .withFaceExpressions()
                        .withAgeAndGender()
                        .withFaceDescriptors()
                        .run();
                }
            }
            else if (this.isMatchPattern(['expressions', 'landmarks', 'descriptors'], this.tokens)) {
                if (t instanceof DetectSingleFaceTask) {
                    return t
                        .withFaceLandmarks()
                        .withFaceExpressions()
                        .withFaceDescriptor()
                        .run();
                }
                else if (t instanceof DetectAllFacesTask) {
                    return t
                        .withFaceLandmarks()
                        .withFaceExpressions()
                        .withFaceDescriptors()
                        .run();
                }
            }
            else if (this.isMatchPattern(['expressions', 'landmarks', 'ageAndGender'], this.tokens)) {
                return t
                    .withFaceLandmarks()
                    .withFaceExpressions()
                    .withAgeAndGender()
                    .run();
            }
            else if (this.isMatchPattern(['expressions', 'landmarks'], this.tokens)) {
                return t
                    .withFaceLandmarks()
                    .withFaceExpressions()
                    .run();
            }
            else if (this.isMatchPattern(['expressions', 'ageAndGender'], this.tokens)) {
                return t
                    .withFaceExpressions()
                    .withAgeAndGender()
                    .run();
            }
            else if (this.isMatchPattern(['expressions'], this.tokens)) {
                return t.withFaceExpressions().run();
            }
            else if (this.isMatchPattern(['landmarks', 'ageAndGender'], this.tokens)) {
                return t
                    .withFaceLandmarks()
                    .withAgeAndGender()
                    .run();
            }
            else if (this.isMatchPattern(['landmarks'], this.tokens)) {
                return t.withFaceLandmarks().run();
            }
            return t.run();
        });
    }
}

const FaceDetectionOptions = new InjectionToken('ngx-face-api-js.face-detection-options');

const ExpressionsFeatureToken = 'expressions';
const LandmarksFeatureToken = 'landmarks';
const DescriptorsFeatureToken = 'descriptors';
const AgeAndGenderToken = 'ageAndGender';

const SsdMobilenetv1Model = 'SsdMobilenetv1Model';
const MtcnnModel = 'MtcnnModel';
const FaceExpressionModel = 'FaceExpressionModel';
const FaceLandmarkModel = 'FaceLandmarkModel';
const FaceRecognitionModel = 'FaceRecognitionModel';
const TinyFaceDetectorModel = 'TinyFaceDetectorModel';
const AgeAndGenderModel = 'AgeAndGenderModel';

const ModelsUrl = new InjectionToken('ngx-face-api-js.models-url');

class ModelLoaderService {
    constructor(modelUrl) {
        this.modelUrl = modelUrl;
        this.loadedModels = [];
        this.loadModels = (requires) => {
            const tokens = [];
            requires.map((r) => {
                tokens.push(r);
            });
            return tokens;
        };
    }
    getReqiredModels(tokens) {
        return [SsdMobilenetv1Model].concat(Object.entries({
            [ExpressionsFeatureToken]: [FaceExpressionModel, SsdMobilenetv1Model],
            [LandmarksFeatureToken]: [FaceLandmarkModel, SsdMobilenetv1Model],
            [DescriptorsFeatureToken]: [FaceRecognitionModel],
            [AgeAndGenderToken]: [AgeAndGenderModel],
        })
            .map(([key, models]) => tokens.includes(key) ? models : [])
            .reduce((a, b) => a.concat(b))
            .filter((v, i, arr) => arr.indexOf(v) === i));
    }
    mapLoadFunction(model) {
        switch (model) {
            case SsdMobilenetv1Model:
                return loadSsdMobilenetv1Model;
            case MtcnnModel:
                return loadMtcnnModel;
            case FaceExpressionModel:
                return loadFaceExpressionModel;
            case FaceLandmarkModel:
                return loadFaceLandmarkModel;
            case FaceRecognitionModel:
                return loadFaceRecognitionModel;
            case TinyFaceDetectorModel:
                return loadTinyFaceDetectorModel;
            case AgeAndGenderModel:
                return loadAgeGenderModel;
        }
    }
    isLoaded(model) {
        return this.loadedModels.includes(model);
    }
    load(...models) {
        return __awaiter(this, void 0, void 0, function* () {
            const loadTargetModels = models.filter(m => this.isLoaded(m) === false);
            yield Promise.all(loadTargetModels
                .map(m => this.mapLoadFunction(m))
                .map(load => load(this.modelUrl)));
            if (loadTargetModels.length >= 0) {
                this.loadedModels = loadTargetModels.concat(this.loadedModels);
            }
        });
    }
    loadForFeature(tokens) {
        return __awaiter(this, void 0, void 0, function* () {
            const reqiredModels = this.getReqiredModels(tokens);
            yield this.load(...reqiredModels);
        });
    }
}
/** @nocollapse */ ModelLoaderService.ɵfac = function ModelLoaderService_Factory(t) { return new (t || ModelLoaderService)(ɵɵinject(ModelsUrl)); };
/** @nocollapse */ ModelLoaderService.ɵprov = ɵɵdefineInjectable({ token: ModelLoaderService, factory: ModelLoaderService.ɵfac });
/*@__PURE__*/ (function () { ɵsetClassMetadata(ModelLoaderService, [{
        type: Injectable
    }], function () { return [{ type: undefined, decorators: [{
                type: Inject,
                args: [ModelsUrl]
            }] }]; }, null); })();

class FaceDetectorService {
    constructor(modelLoader, option) {
        this.modelLoader = modelLoader;
        this.option = option;
    }
    detect(task) {
        if (task.realtime === true) {
            return of(task).pipe(switchMap((t) => __awaiter(this, void 0, void 0, function* () { return yield t.target; })), switchMap(video => fromEvent(video, 'loadeddata')), switchMap(() => this.modelLoader.loadForFeature(task.tokens)), switchMap(() => interval(300)), switchMap(() => task.detect(this.option)), shareReplay(1));
        }
        return of(task).pipe(switchMap((t) => __awaiter(this, void 0, void 0, function* () { return yield t.target; })), switchMap(image => fromEvent(image, 'load')), switchMap(() => __awaiter(this, void 0, void 0, function* () { return yield this.modelLoader.loadForFeature(task.tokens); })), switchMap(() => task.detect(this.option)), shareReplay(1));
    }
}
/** @nocollapse */ FaceDetectorService.ɵfac = function FaceDetectorService_Factory(t) { return new (t || FaceDetectorService)(ɵɵinject(ModelLoaderService), ɵɵinject(FaceDetectionOptions, 8)); };
/** @nocollapse */ FaceDetectorService.ɵprov = ɵɵdefineInjectable({ token: FaceDetectorService, factory: FaceDetectorService.ɵfac });
/*@__PURE__*/ (function () { ɵsetClassMetadata(FaceDetectorService, [{
        type: Injectable
    }], function () { return [{ type: ModelLoaderService }, { type: undefined, decorators: [{
                type: Optional
            }, {
                type: Inject,
                args: [FaceDetectionOptions]
            }] }]; }, null); })();

class FaceDetectorResponseService {
    constructor() {
        this.responseFaceApi = new BehaviorSubject(null);
    }
}
/** @nocollapse */ FaceDetectorResponseService.ɵfac = function FaceDetectorResponseService_Factory(t) { return new (t || FaceDetectorResponseService)(); };
/** @nocollapse */ FaceDetectorResponseService.ɵprov = ɵɵdefineInjectable({ token: FaceDetectorResponseService, factory: FaceDetectorResponseService.ɵfac });
/*@__PURE__*/ (function () { ɵsetClassMetadata(FaceDetectorResponseService, [{
        type: Injectable
    }], function () { return []; }, null); })();

class FaceCompareService {
    constructor(modelLoaderService) {
        this.modelLoaderService = modelLoaderService;
        // test: compate two images
        this.compare = (base64ImgBase, base64ICompare) => {
            return new Promise((resolve) => __awaiter(this, void 0, void 0, function* () {
                const a = new DetectTask({
                    type: 'all',
                    tokens: [],
                    draw: false
                });
                const sourceImageResult = yield this.fetchImage(base64ImgBase);
                const faceMatcher = new FaceMatcher(sourceImageResult);
                const targetImageResult = yield this.fetchImage(base64ICompare);
                yield targetImageResult.forEach(fd => {
                    const bestMatch = faceMatcher.findBestMatch(fd.descriptor);
                    resolve(bestMatch.toString());
                });
            }));
        };
        this.compareDescriptors = (d1, d2) => {
            return new Promise((resolve, reject) => {
                try {
                    let distance = null;
                    let image1 = new Float32Array(Object.values(JSON.parse(d1)));
                    let image2 = new Float32Array(Object.values(JSON.parse(d2)));
                    distance = euclideanDistance(image1, image2);
                    resolve({
                        response: 'success',
                        detail: distance
                    });
                }
                catch (err) {
                    console.log(err);
                    reject({
                        response: 'error',
                        code: 'fatal',
                        detail: err
                    });
                }
            });
        };
        this.fetchImage = (base64Image) => __awaiter(this, void 0, void 0, function* () {
            const img = yield fetchImage(base64Image);
            return yield detectAllFaces(img).withFaceLandmarks().withFaceDescriptors();
        });
    }
}
/** @nocollapse */ FaceCompareService.ɵfac = function FaceCompareService_Factory(t) { return new (t || FaceCompareService)(ɵɵinject(ModelLoaderService)); };
/** @nocollapse */ FaceCompareService.ɵprov = ɵɵdefineInjectable({ token: FaceCompareService, factory: FaceCompareService.ɵfac });
/*@__PURE__*/ (function () { ɵsetClassMetadata(FaceCompareService, [{
        type: Injectable
    }], function () { return [{ type: ModelLoaderService }]; }, null); })();

const _c0 = ["canvas"];
class DetectionResultComponent {
    constructor(task, el, renderer, faceDetector, faceDetectorResponse) {
        this.task = task;
        this.el = el;
        this.renderer = renderer;
        this.faceDetector = faceDetector;
        this.faceDetectorResponse = faceDetectorResponse;
        this.subscription = new Subscription();
        this.resize$ = new Subject();
        this.resetCanvas = () => {
            const context = this.canvas.getContext('2d');
            context.clearRect(0, 0, this.canvas.width, this.canvas.height);
        };
    }
    get canvas() {
        return this.canvasEl.nativeElement;
    }
    onResize() {
        this.resize$.next('onResize');
    }
    convertResultToArray(result) {
        if (Array.isArray(result)) {
            return result;
        }
        return [result];
    }
    ngOnInit() {
        //           this.resize$.pipe(startWith('init')),
        this.subscription.add(combineLatest(this.faceDetector.detect(this.task)).pipe(map(([result]) => this.convertResultToArray(result)))
            .subscribe(result => {
            this.resetCanvas();
            if (this.task.draw) {
                if (result.length === 1) {
                    this.draw(result);
                }
            }
            this.faceDetectorResponse.responseFaceApi.next(result);
        }));
    }
    ngOnDestroy() {
        this.resetCanvas();
        this.subscription.unsubscribe();
    }
    draw(results) {
        return __awaiter(this, void 0, void 0, function* () {
            const target = yield this.task.target;
            let { width, height } = target;
            if (target instanceof HTMLVideoElement) {
                height = target.videoHeight;
                width = target.videoWidth;
            }
            const detectionsForSize = resizeResults(results.map(result => result instanceof FaceDetection ? result : result.detection), { width, height });
            this.canvas.width = width;
            this.canvas.height = height;
            this.renderer.setStyle(this.canvas, 'width', `${width}px`);
            this.renderer.setStyle(this.canvas, 'height', `${height}px`);
            if (this.task.tokens.length >= 1) {
                draw.drawDetections(this.canvas, detectionsForSize);
                const resizeResults$1 = resizeResults(results, { width, height });
                if (this.task.tokens.includes('expressions')) {
                    draw.drawFaceExpressions(this.canvas, resizeResults$1.map(({ detection, expressions }) => ({
                        position: detection.box,
                        expressions,
                    })));
                }
                if (this.task.tokens.includes('landmarks')) {
                    draw.drawFaceLandmarks(this.canvas, resizeResults$1.map(({ landmarks }) => landmarks));
                }
                /**
                 * [
                        `${faceapi.round(age, 0)} years`,
                        `${gender} (${faceapi.round(genderProbability)})`,
                      ],
                */
                if (this.task.tokens.includes('ageAndGender')) {
                    resizeResults$1.forEach(result => {
                        const { age, gender, genderProbability } = result;
                        const text = new draw.DrawTextField([
                            `${age} years`,
                            `${gender} (${genderProbability})`,
                        ], result.detection.box.bottomLeft);
                        text.draw(this.canvas);
                    });
                }
            }
            else {
                draw.drawDetections(this.canvas, detectionsForSize);
            }
        });
    }
}
/** @nocollapse */ DetectionResultComponent.ɵfac = function DetectionResultComponent_Factory(t) { return new (t || DetectionResultComponent)(ɵɵdirectiveInject(DetectTask), ɵɵdirectiveInject(ElementRef), ɵɵdirectiveInject(Renderer2), ɵɵdirectiveInject(FaceDetectorService), ɵɵdirectiveInject(FaceDetectorResponseService)); };
/** @nocollapse */ DetectionResultComponent.ɵcmp = ɵɵdefineComponent({ type: DetectionResultComponent, selectors: [["ng-component"]], viewQuery: function DetectionResultComponent_Query(rf, ctx) { if (rf & 1) {
        ɵɵviewQuery(_c0, true);
    } if (rf & 2) {
        var _t;
        ɵɵqueryRefresh(_t = ɵɵloadQuery()) && (ctx.canvasEl = _t.first);
    } }, hostBindings: function DetectionResultComponent_HostBindings(rf, ctx) { if (rf & 1) {
        ɵɵlistener("resize", function DetectionResultComponent_resize_HostBindingHandler() { return ctx.onResize(); }, false, ɵɵresolveWindow);
    } }, decls: 2, vars: 0, consts: [["canvas", ""]], template: function DetectionResultComponent_Template(rf, ctx) { if (rf & 1) {
        ɵɵelement(0, "canvas", null, 0);
    } }, styles: ["canvas[_ngcontent-%COMP%]{height:100%;width:100%}"] });
/*@__PURE__*/ (function () { ɵsetClassMetadata(DetectionResultComponent, [{
        type: Component,
        args: [{
                templateUrl: './detection-result.component.html',
                styleUrls: ['./detection-result.component.scss'],
            }]
    }], function () { return [{ type: DetectTask }, { type: ElementRef }, { type: Renderer2 }, { type: FaceDetectorService }, { type: FaceDetectorResponseService }]; }, { canvasEl: [{
            type: ViewChild,
            args: ['canvas']
        }], onResize: [{
            type: HostListener,
            args: ['window:resize']
        }] }); })();

class AbstractDetectDirective {
    constructor(el, overlay, injector) {
        this.el = el;
        this.overlay = overlay;
        this.injector = injector;
        this.with = [];
        this.draw = true;
    }
    ngOnInit() {
        this.task = new DetectTask({
            type: this.type,
            tokens: this.with,
            draw: this.draw,
            realtime: this.stream,
        });
    }
    get orverlayPositionStrategy() {
        return this.overlay
            .position()
            .flexibleConnectedTo(this.el)
            .withPositions([
            {
                overlayX: 'start',
                overlayY: 'top',
                originX: 'start',
                originY: 'top',
            },
        ])
            .withFlexibleDimensions(false)
            .withLockedPosition(true);
    }
    createOverlay() {
        const scrollStrategy = this.overlay.scrollStrategies.reposition();
        const config = new OverlayConfig({
            positionStrategy: this.orverlayPositionStrategy,
            scrollStrategy,
            hasBackdrop: false,
        });
        return this.overlay.create(config);
    }
    createInjector() {
        return Injector.create({
            parent: this.injector,
            providers: [
                {
                    provide: DetectTask,
                    useValue: this.task,
                },
            ],
        });
    }
    ngAfterViewInit() {
        this.task.resolveTarget(this.el.nativeElement);
        const overlayRef = this.createOverlay();
        const injector = this.createInjector();
        const portal = new ComponentPortal(DetectionResultComponent, undefined, injector);
        overlayRef.attach(portal);
    }
}
/** @nocollapse */ AbstractDetectDirective.ɵfac = function AbstractDetectDirective_Factory(t) { return new (t || AbstractDetectDirective)(ɵɵdirectiveInject(ElementRef), ɵɵdirectiveInject(Overlay), ɵɵdirectiveInject(Injector)); };
/** @nocollapse */ AbstractDetectDirective.ɵdir = ɵɵdefineDirective({ type: AbstractDetectDirective });
/*@__PURE__*/ (function () { ɵsetClassMetadata(AbstractDetectDirective, [{
        type: Directive
    }], function () { return [{ type: ElementRef }, { type: Overlay }, { type: Injector }]; }, null); })();

class DetectAllFacesImgDirective extends AbstractDetectDirective {
    constructor(el, overlay, injector) {
        super(el, overlay, injector);
        this.with = [];
        this.draw = true;
        this.type = 'all';
        this.stream = false;
    }
}
/** @nocollapse */ DetectAllFacesImgDirective.ɵfac = function DetectAllFacesImgDirective_Factory(t) { return new (t || DetectAllFacesImgDirective)(ɵɵdirectiveInject(ElementRef), ɵɵdirectiveInject(Overlay), ɵɵdirectiveInject(Injector)); };
/** @nocollapse */ DetectAllFacesImgDirective.ɵdir = ɵɵdefineDirective({ type: DetectAllFacesImgDirective, selectors: [["img", "allFaces", ""]], inputs: { with: "with", draw: "draw" }, exportAs: ["faces"], features: [ɵɵInheritDefinitionFeature] });
/*@__PURE__*/ (function () { ɵsetClassMetadata(DetectAllFacesImgDirective, [{
        type: Directive,
        args: [{
                selector: 'img[allFaces]',
                exportAs: 'faces',
            }]
    }], function () { return [{ type: ElementRef }, { type: Overlay }, { type: Injector }]; }, { with: [{
            type: Input
        }], draw: [{
            type: Input
        }] }); })();

class DetectSingleFaceImgDirective extends AbstractDetectDirective {
    constructor(el, overlay, injector) {
        super(el, overlay, injector);
        this.with = [];
        this.draw = true;
        this.type = 'single';
        this.stream = false;
    }
    ngOnInit() {
        this.task = new DetectTask({
            type: this.type,
            tokens: this.with,
            draw: this.draw,
            realtime: this.stream,
        });
    }
}
/** @nocollapse */ DetectSingleFaceImgDirective.ɵfac = function DetectSingleFaceImgDirective_Factory(t) { return new (t || DetectSingleFaceImgDirective)(ɵɵdirectiveInject(ElementRef), ɵɵdirectiveInject(Overlay), ɵɵdirectiveInject(Injector)); };
/** @nocollapse */ DetectSingleFaceImgDirective.ɵdir = ɵɵdefineDirective({ type: DetectSingleFaceImgDirective, selectors: [["img", "singleFace", ""]], inputs: { with: "with", draw: "draw" }, exportAs: ["face"], features: [ɵɵInheritDefinitionFeature] });
/*@__PURE__*/ (function () { ɵsetClassMetadata(DetectSingleFaceImgDirective, [{
        type: Directive,
        args: [{
                selector: 'img[singleFace]',
                exportAs: 'face',
            }]
    }], function () { return [{ type: ElementRef }, { type: Overlay }, { type: Injector }]; }, { with: [{
            type: Input
        }], draw: [{
            type: Input
        }] }); })();

class DetectAllFacesVideoDirective extends AbstractDetectDirective {
    constructor(el, overlay, injector) {
        super(el, overlay, injector);
        this.with = [];
        this.draw = true;
        this.type = 'all';
        this.stream = true;
    }
}
/** @nocollapse */ DetectAllFacesVideoDirective.ɵfac = function DetectAllFacesVideoDirective_Factory(t) { return new (t || DetectAllFacesVideoDirective)(ɵɵdirectiveInject(ElementRef), ɵɵdirectiveInject(Overlay), ɵɵdirectiveInject(Injector)); };
/** @nocollapse */ DetectAllFacesVideoDirective.ɵdir = ɵɵdefineDirective({ type: DetectAllFacesVideoDirective, selectors: [["video", "allFaces", ""]], inputs: { with: "with", draw: "draw" }, exportAs: ["faces"], features: [ɵɵInheritDefinitionFeature] });
/*@__PURE__*/ (function () { ɵsetClassMetadata(DetectAllFacesVideoDirective, [{
        type: Directive,
        args: [{
                selector: 'video[allFaces]',
                exportAs: 'faces',
            }]
    }], function () { return [{ type: ElementRef }, { type: Overlay }, { type: Injector }]; }, { with: [{
            type: Input
        }], draw: [{
            type: Input
        }] }); })();

class NgxFaceApiJsModule {
    static forRoot(options) {
        return {
            ngModule: NgxFaceApiJsModule,
            providers: [
                {
                    provide: ModelsUrl,
                    useValue: options.modelsUrl,
                },
                ModelLoaderService,
                FaceDetectorService,
                ...[
                    options.faceDetectionOptions
                        ? {
                            provide: FaceDetectionOptions,
                            useValue: options.faceDetectionOptions,
                        }
                        : [],
                ],
            ],
        };
    }
}
/** @nocollapse */ NgxFaceApiJsModule.ɵmod = ɵɵdefineNgModule({ type: NgxFaceApiJsModule });
/** @nocollapse */ NgxFaceApiJsModule.ɵinj = ɵɵdefineInjector({ factory: function NgxFaceApiJsModule_Factory(t) { return new (t || NgxFaceApiJsModule)(); }, providers: [FaceDetectorResponseService, FaceCompareService], imports: [[BrowserModule, OverlayModule, PortalModule]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && ɵɵsetNgModuleScope(NgxFaceApiJsModule, { declarations: [DetectionResultComponent,
        DetectAllFacesImgDirective,
        DetectSingleFaceImgDirective,
        DetectAllFacesVideoDirective], imports: [BrowserModule, OverlayModule, PortalModule], exports: [DetectAllFacesImgDirective,
        DetectSingleFaceImgDirective,
        DetectAllFacesVideoDirective] }); })();
/*@__PURE__*/ (function () { ɵsetClassMetadata(NgxFaceApiJsModule, [{
        type: NgModule,
        args: [{
                declarations: [
                    DetectionResultComponent,
                    DetectAllFacesImgDirective,
                    DetectSingleFaceImgDirective,
                    DetectAllFacesVideoDirective,
                ],
                imports: [BrowserModule, OverlayModule, PortalModule],
                exports: [
                    DetectAllFacesImgDirective,
                    DetectSingleFaceImgDirective,
                    DetectAllFacesVideoDirective,
                ],
                providers: [FaceDetectorResponseService, FaceCompareService],
                entryComponents: [DetectionResultComponent],
            }]
    }], null, null); })();

/*
 * Public API Surface of ngx-face-api-js
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AgeAndGenderModel, AgeAndGenderToken, DescriptorsFeatureToken, DetectAllFacesImgDirective, DetectAllFacesVideoDirective, DetectSingleFaceImgDirective, DetectTask, ExpressionsFeatureToken, FaceCompareService, FaceDetectionOptions, FaceDetectorResponseService, FaceDetectorService, FaceExpressionModel, FaceLandmarkModel, FaceRecognitionModel, LandmarksFeatureToken, ModelLoaderService, ModelsUrl, MtcnnModel, NgxFaceApiJsModule, SsdMobilenetv1Model, TinyFaceDetectorModel };
//# sourceMappingURL=akeela-ngx-face-api-js.js.map
